import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import axios from "axios";
import { FaUserFriends, FaGlobe, FaTachometerAlt, FaTrophy, FaClock, FaHandHoldingUsd } from "react-icons/fa";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Import ABI z src/abi
import CRWABI from "../abi/Crown.json";
import CUSDABI from "../abi/CUSD.json";
import DEUSABI from "../abi/DEUS.json";

// Adresy kontraktů
const CRW_ADDRESS = "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03";
const CUSD_ADDRESS = "0x16b6eCed5e669EAA934fB4C82F0353ab371420fa";
const DEUS_ADDRESS = "0x9a1EE4cC684b75eb847F2CCBDf7DeB26F8f8D6F5";

// Blok nasazení kontraktu
const CONTRACT_DEPLOYMENT_BLOCK = 0; 

// Mapa zemí na souřadnice hlavních měst
const countryCoordinates = {
    "US": [38.8951, -77.0364], // Washington, D.C.
    "GB": [51.5085, -0.1257],  // Londýn
    "JP": [35.6895, 139.6917], // Tokio
    "DE": [52.5200, 13.4050],  // Berlín
    "FR": [48.8534, 2.3488],   // Paříž
    "CN": [39.9075, 116.3972], // Peking
    "IN": [28.6139, 77.2090],  // Nové Dillí
    "BR": [-15.8267, -47.9218],// Brasília
    "UNKNOWN": [0, 0]          // Fallback pro neznámé země (nepoužijeme přímo)
};

// Seznam klíčů zemí bez "UNKNOWN" pro náhodné rozložení
const availableCountries = Object.keys(countryCoordinates).filter(key => key !== "UNKNOWN");

const Dashboard = ({ provider }) => {
    const [totalValidators, setTotalValidators] = useState(0);
    const [totalStakers, setTotalStakers] = useState(0);
    const [activeLoans, setActiveLoans] = useState(0);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [publicUserLoans, setPublicUserLoans] = useState(0);
    const [validatorsData, setValidatorsData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            if (provider) {
                try {
                    const crwContract = new ethers.Contract(CRW_ADDRESS, CRWABI, provider);
                    const cusdContract = new ethers.Contract(CUSD_ADDRESS, CUSDABI, provider);
                    const deusContract = new ethers.Contract(DEUS_ADDRESS, DEUSABI, provider);

                    // Total Validators
                    const [validators] = await crwContract.getValidators();
                    console.log("Načtené adresy validatorů:", validators); // Ladění
                    setTotalValidators(validators.length);

                    // Total Stakers
                    const stakedFilter = crwContract.filters.Staked();
                    const unstakedFilter = crwContract.filters.Unstaked();
                    const [stakedEvents, unstakedEvents] = await Promise.all([
                        crwContract.queryFilter(stakedFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        crwContract.queryFilter(unstakedFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest")
                    ]);

                    const stakers = new Set(stakedEvents.map(event => event.args.staker));
                    unstakedEvents.forEach(event => stakers.delete(event.args.staker));
                    setTotalStakers(stakers.size);

                    // Active Loans
                    const loanCreatedFilter = crwContract.filters.LoanCreated();
                    const loanRepaidFilter = crwContract.filters.LoanRepaid();
                    const [loanCreatedEvents, loanRepaidEvents] = await Promise.all([
                        crwContract.queryFilter(loanCreatedFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        crwContract.queryFilter(loanRepaidFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest")
                    ]);

                    const activeLoansCount = loanCreatedEvents.length - loanRepaidEvents.length;
                    setActiveLoans(activeLoansCount >= 0 ? activeLoansCount : 0);

                    // Public User Loans
                    const userLoanCreatedFilter = crwContract.filters.UserLoanCreated();
                    const createdEvents = await crwContract.queryFilter(userLoanCreatedFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest");

                    let publicUserLoansCount = 0;
                    const lenders = new Set(createdEvents.map(event => event.args.lender));

                    for (const lender of lenders) {
                        try {
                            const loan = await crwContract.userLoans(lender);
                            console.log(`Lender: ${lender}, Loan:`, loan); // Ladící výstup
                            if (
                                ethers.formatEther(loan.loanAmount) !== "0.0" &&
                                !loan.isActive &&
                                loan.lender !== ethers.ZeroAddress
                            ) {
                                publicUserLoansCount++;
                            }
                        } catch (error) {
                            console.warn(`Chyba při načítání půjčky pro lendera ${lender}:`, error);
                        }
                    }
                    console.log(`Celkový počet veřejných půjček: ${publicUserLoansCount}`); // Ladící výstup
                    setPublicUserLoans(publicUserLoansCount);

                    // Total Transactions
                    const crwTransferFilter = crwContract.filters.Transfer();
                    const tokensBoughtFilter = crwContract.filters.TokensBought();
                    const tokensSoldFilter = crwContract.filters.TokensSold();
                    const tokensBoughtERC20Filter = crwContract.filters.TokensBoughtWithERC20();
                    const tokensSoldERC20Filter = crwContract.filters.TokensSoldForERC20();

                    const [crwTransferEvents, tokensBoughtEvents, tokensSoldEvents, tokensBoughtERC20Events, tokensSoldERC20Events] = await Promise.all([
                        crwContract.queryFilter(crwTransferFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        crwContract.queryFilter(tokensBoughtFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        crwContract.queryFilter(tokensSoldFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        crwContract.queryFilter(tokensBoughtERC20Filter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        crwContract.queryFilter(tokensSoldERC20Filter, CONTRACT_DEPLOYMENT_BLOCK, "latest")
                    ]);

                    const crwTxCount = crwTransferEvents.length + tokensBoughtEvents.length + tokensSoldEvents.length +
                                      tokensBoughtERC20Events.length + tokensSoldERC20Events.length;

                    const cusdTransferFilter = cusdContract.filters.Transfer();
                    const mintFilter = cusdContract.filters.Mint();
                    const burnFilter = cusdContract.filters.Burn();
                    const buyCUSDFilter = cusdContract.filters.BuyCUSD();

                    const [cusdTransferEvents, mintEvents, burnEvents, buyCUSDEvents] = await Promise.all([
                        cusdContract.queryFilter(cusdTransferFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        cusdContract.queryFilter(mintFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        cusdContract.queryFilter(burnFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        cusdContract.queryFilter(buyCUSDFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest")
                    ]);

                    const cusdTxCount = cusdTransferEvents.length + mintEvents.length + burnEvents.length + buyCUSDEvents.length;

                    const deusTransferFilter = deusContract.filters.Transfer();
                    const deusMintFilter = deusContract.filters.Mint();
                    const deusBurnFilter = deusContract.filters.Burn();
                    const buyDEUSFilter = deusContract.filters.BuyDEUS();

                    const [deusTransferEvents, deusMintEvents, deusBurnEvents, buyDEUSEvents] = await Promise.all([
                        deusContract.queryFilter(deusTransferFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        deusContract.queryFilter(deusMintFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        deusContract.queryFilter(deusBurnFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest"),
                        deusContract.queryFilter(buyDEUSFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest")
                    ]);

                    const deusTxCount = deusTransferEvents.length + deusMintEvents.length + deusBurnEvents.length + buyDEUSEvents.length;

                    const totalTxCount = crwTxCount + cusdTxCount + deusTxCount;
                    setTotalTransactions(totalTxCount);

                    // Validator Map
                    const validatorAddedFilter = crwContract.filters.ValidatorAdded();
                    const validatorAddedEvents = await crwContract.queryFilter(validatorAddedFilter, CONTRACT_DEPLOYMENT_BLOCK, "latest");

                    const validatorData = [];
                    for (const validatorAddress of validators) {
                        try {
                            const addedEvent = validatorAddedEvents
                                .filter(event => event.args.validatorAddress.toLowerCase() === validatorAddress.toLowerCase())
                                .sort((a, b) => b.blockNumber - a.blockNumber)[0];

                            let countryCode = "UNKNOWN";
                            let position = [0, 0];

                            if (addedEvent) {
                                const tx = await provider.getTransaction(addedEvent.transactionHash);
                                if (tx && tx.from) {
                                    try {
                                        const response = await axios.get(`http://ip-api.com/json/${tx.from}`);
                                        if (response.data.status === "success") {
                                            countryCode = response.data.countryCode || "UNKNOWN";
                                        }
                                    } catch (geoError) {
                                        console.warn(`GeoIP chyba pro adresu ${tx.from}:`, geoError);
                                    }
                                }
                            }

                            // Pokud je countryCode "UNKNOWN", přiřaď náhodnou pozici
                            if (countryCode === "UNKNOWN") {
                                const randomCountry = availableCountries[Math.floor(Math.random() * availableCountries.length)];
                                position = countryCoordinates[randomCountry];
                            } else {
                                position = countryCoordinates[countryCode] || countryCoordinates["UNKNOWN"];
                            }

                            validatorData.push({
                                address: validatorAddress,
                                position,
                                country: countryCode
                            });
                        } catch (error) {
                            console.warn(`Chyba při zpracování validátora ${validatorAddress}:`, error);
                            const randomCountry = availableCountries[Math.floor(Math.random() * availableCountries.length)];
                            validatorData.push({
                                address: validatorAddress,
                                position: countryCoordinates[randomCountry],
                                country: "UNKNOWN"
                            });
                        }
                    }
                    console.log("Validator Data:", validatorData); // Ladící výstup
                    setValidatorsData(validatorData);
                } catch (error) {
                    console.error("Error fetching dashboard data:", error);
                }
            }
        };

        fetchData();
    }, [provider]);

    return (
        <div>
            <h2>
                <FaTachometerAlt className="icon" /> Dashboard
            </h2>
            <div className="dashboard-grid">
                <div className="stats-box">
                    <h3><FaUserFriends className="icon" /> Total Validators</h3>
                    <p>{totalValidators}</p>
                </div>
                <div className="stats-box">
                    <h3><FaTrophy className="icon" /> Total Stakers</h3>
                    <p>{totalStakers}</p>
                </div>
                <div className="stats-box">
                    <h3><FaGlobe className="icon" /> Active Loans</h3>
                    <p>{activeLoans}</p>
                </div>
                <div className="stats-box">
                    <h3><FaHandHoldingUsd className="icon" /> Public User Loans</h3>
                    <p>{publicUserLoans}</p>
                </div>
                <div className="stats-box">
                    <h3><FaClock className="icon" /> Total Transactions</h3>
                    <p>{totalTransactions}</p>
                </div>
            </div>
            <div className="map-container">
                <h3><FaGlobe className="icon" /> Validator Map</h3>
                <MapContainer center={[51.505, -0.09]} zoom={2} style={{ height: "400px", width: "100%" }}>
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {validatorsData.map((validator, idx) => (
                        <Marker key={idx} position={validator.position}>
                            <Popup>
                                Validator: {validator.address}<br />
                                Country: {validator.country}
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>
            </div>
        </div>
    );
};

export default Dashboard;